import { useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";

const testimonials = [
  {
    name: "木叶の疾风",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20style%20boy%20avatar%20with%20orange%20hair%20and%20ninja%20headband%20friendly%20smile%20simple%20background&width=100&height=100&seq=avatar-01&orientation=squarish",
    rating: 5,
    comment: "真的太棒了！从第一话开始一口气读到疾风传，画质超级清晰，加载速度也很快。作为一个十多年的火影老粉，终于找到了一个完美的重温平台。强烈推荐给所有火影迷！",
    date: "2024-08-01",
  },
  {
    name: "晓の朱雀",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20style%20character%20avatar%20with%20red%20cloud%20cloak%20cool%20expression%20dark%20background&width=100&height=100&seq=avatar-02&orientation=squarish",
    rating: 5,
    comment: "网站做得非常用心，分类很清晰，每一话都有详细的简介和标签。最重要的是完全免费，对于我们这些学生党来说太友好了。希望网站越做越好！",
    date: "2024-07-28",
  },
  {
    name: "砂隠れの風",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20style%20girl%20avatar%20with%20blonde%20hair%20gentle%20smile%20warm%20lighting%20simple%20background&width=100&height=100&seq=avatar-03&orientation=squarish",
    rating: 5,
    comment: "手机端阅读体验非常流畅！通勤路上就能重温中忍考试的经典剧情。每一话的评论和讨论区也很有意思，能看到很多同好的精彩分析。已经推荐给身边所有看火影的朋友了。",
    date: "2024-07-20",
  },
  {
    name: "霧隠れの白",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20style%20character%20avatar%20with%20dark%20hair%20serene%20expression%20soft%20light%20simple%20background&width=100&height=100&seq=avatar-04&orientation=squarish",
    rating: 5,
    comment: "感谢这个平台让我重新找回了少年时代追火影的感动。终结谷之战看一次哭一次，鸣人和佐助的羁绊真的太虐了。网站的设计也很美观，阅读体验一流！",
    date: "2024-07-15",
  },
  {
    name: "雲隠れの雷",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20style%20boy%20avatar%20with%20spiky%20hair%20energetic%20expression%20bright%20background&width=100&height=100&seq=avatar-05&orientation=squarish",
    rating: 4,
    comment: "整体的阅读体验非常好，画面高清，加载速度也快。经典战役的剧集整理得很有条理，方便按篇章重温。希望能增加更多的番外内容和角色资料！",
    date: "2024-07-10",
  },
  {
    name: "木ノ葉の桜",
    avatar: "https://readdy.ai/api/search-image?query=Anime%20style%20girl%20avatar%20with%20pink%20hair%20cute%20smile%20cherry%20blossom%20background&width=100&height=100&seq=avatar-06&orientation=squarish",
    rating: 5,
    comment: "终于找到了一个画质好的火影漫画网站！之前在其他平台看，图片都很模糊。这里的每一页都超级清晰，色彩还原度很高。作为火影的忠实粉丝，这个网站我已经收藏啦！",
    date: "2024-07-05",
  },
];

export default function AboutPage() {
  const { t } = useTranslation();

  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get("contact_alt") as string || "").trim();
    if (honeypotValue) {
      setFormState("success");
      form.reset();
      return;
    }

    setFormState("submitting");
    setFormError("");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9rccf4nlsngrm4lma60", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== "contact_alt")
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormState("success");
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          "提交失败";
        if (serverMsg.includes("spam") || serverMsg.includes("form data is spam")) {
          setFormError("提交失败，请稍后重试");
        } else {
          setFormError(serverMsg);
        }
        setFormState("error");
      }
    } catch {
      setFormError("网络错误，请检查网络连接后重试");
      setFormState("error");
    }
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "火影漫画",
    url: "https://naruto-manga.com",
    description: "火影漫画官方正版全集在线阅读平台",
    foundingDate: "2024",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      availableLanguage: ["Chinese"],
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: t("breadcrumb.home"), href: "/" },
              { label: t("about.title") },
            ]}
          />

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
          />

          {/* Page Title */}
          <div className="mb-12">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {t("about.title")}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              了解火影漫画在线平台的故事、理念与团队，以及来自广大火影迷的真实评价。
            </p>
          </div>

          {/* Brand Story */}
          <section className="mb-16">
            <h2 className="text-xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center bg-primary-100/70 rounded-md">
                <i className="ri-book-open-line text-primary-600"></i>
              </div>
              {t("about.brand.title")}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  火影漫画在线平台成立于2024年，由一群热爱火影忍者的资深漫迷共同创建。我们深知《火影忍者》在每一位漫迷心中的特殊地位——它不仅仅是一部漫画作品，更是一代人青春的记忆与热血的象征。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  鸣人从一个被全村孤立的"吊车尾"，凭借永不放弃的精神和珍视同伴的心，最终成长为受人尊敬的七代目火影。这种"火之意志"深深影响了全世界的读者，也激励着我们创建了这个平台。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  我们的使命是为华语地区的火影迷提供最优质、最完整的正版漫画阅读体验。我们精心整理了全部章节，按照篇章分类，让你可以轻松地按照故事线重温这部不朽经典。无论是第一次接触火影的新读者，还是已经看了无数遍的老粉丝，都能在这里找到属于自己的感动。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden h-64 lg:h-auto">
                <img
                  src="https://readdy.ai/api/search-image?query=Naruto%20manga%20volumes%20stacked%20on%20wooden%20desk%20with%20warm%20reading%20lamp%20light%20cozy%20atmosphere%20anime%20style%20illustration%20nostalgic%20vibe&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="火影漫画品牌故事"
                  title="火影漫画在线平台"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="mb-16 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-5 bg-background-50 border border-background-200/70 rounded-lg text-center">
              <div className="text-2xl font-bold text-primary-500 mb-1">700</div>
              <p className="text-xs text-foreground-500">全部话数</p>
            </div>
            <div className="p-5 bg-background-50 border border-background-200/70 rounded-lg text-center">
              <div className="text-2xl font-bold text-accent-500 mb-1">8</div>
              <p className="text-xs text-foreground-500">篇章分类</p>
            </div>
            <div className="p-5 bg-background-50 border border-background-200/70 rounded-lg text-center">
              <div className="text-2xl font-bold text-primary-500 mb-1">15年</div>
              <p className="text-xs text-foreground-500">连载时间</p>
            </div>
            <div className="p-5 bg-background-50 border border-background-200/70 rounded-lg text-center">
              <div className="text-2xl font-bold text-accent-500 mb-1">2.5亿</div>
              <p className="text-xs text-foreground-500">全球销量</p>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-16">
            <h2 className="text-xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center bg-accent-100/70 rounded-md">
                <i className="ri-heart-line text-accent-600"></i>
              </div>
              {t("about.reputation.title")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((item, idx) => (
                <div
                  key={idx}
                  className="p-5 bg-background-50 border border-background-200/70 rounded-lg"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={item.avatar}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: item.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-xs text-amber-400"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    "{item.comment}"
                  </p>
                  <p className="text-xs text-foreground-400">{item.date}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="mb-16">
            <h2 className="text-xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center bg-secondary-100/70 rounded-md">
                <i className="ri-mail-line text-secondary-600"></i>
              </div>
              {t("about.contact.title")}
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {t("about.contact.subtitle")}
                </p>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-full">
                      <i className="ri-mail-line text-foreground-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">邮箱</p>
                      <p className="text-sm text-foreground-800">contact@naruto-manga.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-full">
                      <i className="ri-user-smile-line text-foreground-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">客服时间</p>
                      <p className="text-sm text-foreground-800">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                {formState === "success" ? (
                  <div className="p-8 bg-background-50 border border-background-200/70 rounded-lg text-center">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-green-100 rounded-full">
                      <i className="ri-check-line text-xl text-green-600"></i>
                    </div>
                    <p className="text-sm font-semibold text-foreground-900">
                      {t("about.contact.success")}
                    </p>
                    <button
                      onClick={() => setFormState("idle")}
                      className="mt-4 text-sm text-primary-500 hover:text-primary-600"
                    >
                      发送新留言
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="p-6 bg-background-50 border border-background-200/70 rounded-lg"
                  >
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t("about.contact.name")}
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t("about.contact.email")}
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t("about.contact.message")}
                      </label>
                      <textarea
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>

                    {/* Honeypot */}
                    <input
                      type="text"
                      name="contact_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10 pointer-events-none"
                    />

                    {formState === "error" && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">{formError || t("about.contact.error")}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formState === "submitting"}
                      className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      {formState === "submitting" ? "提交中..." : t("about.contact.submit")}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}